"use client";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Images, Icons } from "@/app/assets/shared";
import PrimaryBtn from "@/components/PrimaryBtn";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { CLIENT_ENTRIES, ClientEntry } from "./data";
import { ensureGsap } from "@/lib/EnsureGsap";

const AUTO_SWITCH_INTERVAL_MS = 6000;

const REVEAL_ITEM_CLASS = "case-study-reveal-item";

const ChallengeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0a0a0a"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const SolutionIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0a0a0a"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5.75.75 1.23 1.5 1.41 2.5" />
  </svg>
);

const ImpactIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0a0a0a"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export default function SuccessStoryShowcase({
  clients = CLIENT_ENTRIES,
}: {
  clients?: ClientEntry[];
}) {
  const isMobile = useIsMobile();
  const [activeClientId, setActiveClientId] = useState<
    ClientEntry["id"] | undefined
  >(clients[0]?.id);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingRef = useRef(AUTO_SWITCH_INTERVAL_MS);
  const timerStartRef = useRef(0);
  const isPausedRef = useRef(false);
  const activeFillRef = useRef<HTMLSpanElement | null>(null);
  const contentBoxRef = useRef<HTMLDivElement | null>(null);
  const imageWrapRef = useRef<HTMLDivElement | null>(null);

  const [openSection, setOpenSection] = useState<
    "challenge" | "solution" | "impact" | null
  >("challenge");

  const activeClient =
    clients.find((client) => client.id === activeClientId) ?? clients[0];
  const caseStudy = activeClient?.caseStudy;

  useEffect(() => {
    if (!clients?.length || clients.length < 2) return;

    const currentIndex = clients.findIndex((c) => c.id === activeClientId);
    if (currentIndex === -1) return;

    const nextIndex = (currentIndex + 1) % clients.length;
    const nextSrc = clients[nextIndex]?.caseStudy?.image;
    if (!nextSrc || typeof nextSrc !== "string") return;

    const img = new window.Image();
    img.src = nextSrc;
  }, [activeClientId, clients]);

  useEffect(() => {
    if (!clients?.length || clients.length < 2) return;

    remainingRef.current = AUTO_SWITCH_INTERVAL_MS;

    const advance = () => {
      setActiveClientId((current) => {
        const currentIndex = clients.findIndex((c) => c.id === current);
        const nextIndex = (currentIndex + 1) % clients.length;
        return clients[nextIndex]?.id ?? current;
      });
    };

    const schedule = () => {
      timerStartRef.current = Date.now();
      timeoutRef.current = setTimeout(advance, remainingRef.current);
    };

    if (!isPausedRef.current) {
      schedule();
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [activeClientId, clients]);

  const pauseAutoAdvance = () => {
    if (isPausedRef.current) return;
    isPausedRef.current = true;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // Record how much of this cycle's time is left so resuming continues
    // from here instead of restarting the full interval.
    const elapsed = Date.now() - timerStartRef.current;
    remainingRef.current = Math.max(remainingRef.current - elapsed, 0);

    if (activeFillRef.current) {
      activeFillRef.current.style.animationPlayState = "paused";
    }
  };

  const resumeAutoAdvance = () => {
    if (!isPausedRef.current) return;
    isPausedRef.current = false;

    timerStartRef.current = Date.now();
    timeoutRef.current = setTimeout(() => {
      setActiveClientId((current) => {
        const currentIndex = clients.findIndex((c) => c.id === current);
        const nextIndex = (currentIndex + 1) % clients.length;
        return clients[nextIndex]?.id ?? current;
      });
    }, remainingRef.current);

    if (activeFillRef.current) {
      activeFillRef.current.style.animationPlayState = "running";
    }
  };

  const handleManualSelect = (id: ClientEntry["id"]) => {
    setActiveClientId(id);
  };

  const toggleSection = (key: "challenge" | "solution" | "impact") => {
    setOpenSection((current) => (current === key ? null : key));
  };

  useEffect(() => {
    setOpenSection("challenge");
  }, [activeClientId]);

  useEffect(() => {
    let cancelled = false;

    ensureGsap().then(({ gsap }) => {
      if (cancelled) return;

      const items = contentBoxRef.current?.querySelectorAll(
        `.${REVEAL_ITEM_CLASS}`,
      );

      if (items?.length) {
        gsap.fromTo(
          items,
          { opacity: 0, y: 18 },
          {
            opacity: 1,
            y: 0,
            duration: 0.5,
            ease: "power3.out",
            stagger: 0.07,
          },
        );
      }

      if (imageWrapRef.current) {
        gsap.fromTo(
          imageWrapRef.current,
          { opacity: 0, scale: 1.08 },
          { opacity: 1, scale: 1, duration: 0.7, ease: "power3.out" },
        );
      }
    });

    return () => {
      cancelled = true;
    };
  }, [activeClientId]);

  const renderSection = (
    key: "challenge" | "solution" | "impact",
    icon: React.ReactNode,
    label: string,
    body: React.ReactNode,
  ) => {
    const isOpen = !isMobile || openSection === key;

    return (
      <div className={`${REVEAL_ITEM_CLASS} flex flex-col gap-2`}>
        <button
          type="button"
          onClick={() => isMobile && toggleSection(key)}
          aria-expanded={isOpen}
          className="flex w-full items-center justify-between gap-2 text-left lg:pointer-events-none"
        >
          <span className="flex items-center gap-2">
            {icon}
            <span className="text-[18px] font-semibold text-[#0a0a0a] lg:text-[20px]">
              {label}
            </span>
          </span>
          <svg
            className={`h-4 w-4 shrink-0 text-[#0a0a0a] transition-transform duration-200 lg:hidden ${
              isOpen ? "rotate-180" : ""
            }`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        {isOpen && (
          <div className="pl-7 text-[16px] leading-[150%] text-[#5a5a5a] lg:text-[18px] lg:leading-[160%]">
            {body}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="success-story-showcase relative w-full overflow-hidden bg-[#000000]">
      <style>{`
        @keyframes tabFillProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>

      {/* Texture background */}
      <div className="texture-bg pointer-events-none absolute left-0 top-0 z-0 h-full w-full mix-blend-overlay">
        {!isMobile && (
          <div className="absolute inset-0 z-0">
            <Image
              src={Images?.texture}
              alt="texture"
              fill
              className="object-cover mix-blend-overlay opacity-60"
              sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
            />
          </div>
        )}
      </div>

      {/* Glow accents */}
      <div className="absolute right-0 top-0 h-[907px] w-[907px] -translate-y-1/3 translate-x-1/3 rounded-full bg-[#225bd4]/40 blur-[333px]" />
      <div className="absolute bottom-0 left-0 h-[907px] w-[907px] -translate-x-1/3 translate-y-1/3 rounded-full bg-[#225bd4]/40 blur-[333px]" />

      <div className="relative z-[1] mx-auto w-[calc(100%_-_10%)]  py-[50px] lg:py-[100px]">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-md font-medium uppercase tracking-tight text-white">
            Our Success Stories
          </p>
          <h2 className="clash-font max-w-[24ch] text-[length:var(--section-title)] font-medium leading-[110%]">
            <span className="text-white">Your Idea, </span>
            <span className="text-[#225BD4]">Our Expertise:</span>
          </h2>
          <p className="max-w-[62ch] text-[length:var(--hero-para)] font-normal leading-[150%] text-white/50">
            Our work speaks louder than words. We collaborate with visionary
            brands to solve complex challenges and deliver measurable outcomes.
          </p>
        </div>

        {/* Outer #ECF0F6 box */}
        {caseStudy && (
          <div
            className="mt-10 w-full rounded-none bg-[#ECF0F6] p-3 lg:mt-16 lg:p-8"
            onMouseEnter={pauseAutoAdvance}
            onMouseLeave={resumeAutoAdvance}
          >
            <div
              ref={contentBoxRef}
              className="flex w-full flex-col overflow-hidden rounded-none bg-white lg:flex-row"
            >
              {/* Image: shown first on mobile (order-1), right column on
                  desktop (lg:order-2, 40% width) */}
              <div className="relative order-1 w-full lg:order-2 lg:w-[40%]">
                <div
                  ref={imageWrapRef}
                  className="relative aspect-[16/9] w-full lg:aspect-auto lg:h-full"
                >
                  {caseStudy.image && (
                    <Image
                      key={caseStudy.image}
                      src={caseStudy.image}
                      alt={caseStudy.title}
                      fill
                      sizes="(max-width: 1024px) 100vw, 40vw"
                      className="object-cover"
                    />
                  )}
                </div>
              </div>

              {/* Content: shown second on mobile (order-2), left column on
                  desktop (lg:order-1, 60% width) */}
              <div className="order-2 flex w-full flex-col gap-4 p-6 lg:order-1 lg:w-[60%] lg:gap-6 lg:p-12">
                <h3
                  className={`${REVEAL_ITEM_CLASS} clash-font text-xl font-medium leading-[120%] text-[#0a0a0a] lg:text-4xl`}
                >
                  {caseStudy.title}
                </h3>

                {/* Tags */}
                {caseStudy.tags?.length > 0 && (
                  <div className={`${REVEAL_ITEM_CLASS} flex flex-wrap gap-2`}>
                    {caseStudy.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="rounded-full bg-[#ECF0F6] px-4 py-2 text-[13px] font-medium text-[#00040B] lg:text-[15px]"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Challenge / Solution / Impact — single-open accordion on
                    mobile, always-expanded on desktop (see renderSection). */}
                {renderSection(
                  "challenge",
                  <ChallengeIcon />,
                  "The Challenge",
                  caseStudy.challenge,
                )}

                {renderSection(
                  "solution",
                  <SolutionIcon />,
                  "The Solution",
                  caseStudy.solution,
                )}

                {renderSection(
                  "impact",
                  <ImpactIcon />,
                  "The Impact",
                  <ul className="inline-grid grid-cols-1 gap-x-12 gap-y-1 sm:grid-cols-[max-content_max-content]">
                    {caseStudy.impact.map((point) => (
                      <li
                        key={point.id}
                        className="list-disc marker:text-[#5a5a5a]"
                      >
                        {point.text}
                      </li>
                    ))}
                  </ul>,
                )}

                {/* CTA — opens/downloads the case-study PDF for this client */}
                <div className={`${REVEAL_ITEM_CLASS} mt-2 w-fit`}>
                  <a
                    href={caseStudy.caseStudyHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <PrimaryBtn
                      text="View Case Study"
                      iconSrc={Icons?.chevron}
                      dark={false}
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Client logos row — click to switch active case study */}
        {clients?.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {clients.map((client) => {
              const isActive = client.id === activeClientId;
              return (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => handleManualSelect(client.id)}
                  aria-pressed={isActive}
                  aria-label={client.name}
                  className={`relative flex h-[54px] items-center justify-center overflow-hidden rounded-full px-8 transition-colors ${
                    isActive
                      ? "bg-[#225BD4]/30"
                      : "border border-white/20 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  {isActive && (
                    <span
                      key={client.id}
                      ref={activeFillRef}
                      className="pointer-events-none absolute inset-y-0 left-0 z-0 bg-[#225BD4]"
                      style={{
                        animation: `tabFillProgress ${AUTO_SWITCH_INTERVAL_MS}ms linear forwards`,
                        animationPlayState: isPausedRef.current
                          ? "paused"
                          : "running",
                      }}
                    />
                  )}

                  {client.logo ? (
                    <Image
                      src={client.logo}
                      alt={client.name}
                      width={120}
                      height={30}
                      className={`relative z-[1] h-[26px] lg:h-[30px] w-auto object-contain transition-opacity ${
                        isActive ? "opacity-100" : "opacity-50 hover:opacity-80"
                      }`}
                    />
                  ) : (
                    <span
                      className={`relative z-[1] text-sm font-medium ${
                        isActive ? "text-white" : "text-white/40"
                      }`}
                    >
                      {client.name}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
